import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import { Embedded } from '@data/models/embed.model';
import { Inventory } from '@data/models/inventory.model';
import { ListMetadataKeys } from '@data/models/metadata.model';
import { FilterParams } from '@claranet/node-search-filter';
import {
    configuration as projectConfiguration,
    embed as projectEmbed,
    EmbedProject,
    Project,
    ProjectServiceCatalog,
    UnprovisionProjectInventoryRequest,
    BulkEditProjectResponse,
    Operation,
} from '@data/models/project.model';
import { Tag } from '@data/models/tag.model';
import { COMPANY_SERVICE_API_ROOT } from '@data/services/company.service';
import { GroupService } from '@shared/components/group/group.service';
import { TagService } from '@shared/components/tag/tag.service';
import { toParams } from '@shared/utils/query-to-params';
import { isEmpty, uniq, flatten } from 'lodash';
import {
    combineLatest,
    Observable,
    of,
} from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';
import { MetadataService } from './metadata.service';
import { environment } from '@env/environment';

export interface ProjectInventories {
    id: string;
    name: string;
    environment: string;
    tags: string[];
}

interface GetInventoriesOptions {
    fields: string[];
    filter: string;
    page: number;
    limit: number;
    sort: string;
}

export const PROJECT_SERVICE_API_ROOT = '/core/v1/projects';
export const PROJECT_SERVICE_ASSET_API_ROOT = '/asset/v1/projects';
export const UNPROVISION_CLOUD_FUNCTION_API_ROOT  = '/unprovision';
export const LIMIT_USER_FORM_PROJECT = 1000;
@Injectable()
export class ProjectService implements TagService, GroupService {

    private errors;

    constructor(
        private readonly errorService: ErrorService,
        private readonly http: HttpClient,
        private readonly metadataService: MetadataService,
    ) {
        this.errors = this.errorService.projectServiceErrors;
    }

    public findProjects<T extends projectEmbed>(filterParams: FilterParams, embedFields: T[] = []): Observable<EmbedProject<T>[]> {
        const embeds = uniq(embedFields.map((it) => projectConfiguration[it].embed));
        const params = toParams({ ...filterParams, embed: !isEmpty(embeds) && embeds.join(',') });
        return this.http.get<{ projects: Project[]; embedded: Embedded }>(PROJECT_SERVICE_API_ROOT, { params }).pipe(
            map(({ projects, embedded }) => Project.toCustomTicketList(projects, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedProject<T>[]>(this.errors, 'Récupération des projets', []))
        );
    }

    public getProjectServiceCatalogs(projectId: string): Observable<ProjectServiceCatalog[]> {
        return this.http.get<{ serviceCatalogs: ProjectServiceCatalog[] }>(`${PROJECT_SERVICE_API_ROOT}/${projectId}/service-catalogs`).pipe(
            map(({ serviceCatalogs }) => serviceCatalogs),
            catchError(this.errorService.errorHandler<ProjectServiceCatalog[]>(this.errors, 'Récupération du catalogue de services', null))
        );
    }

    public updateProjectServiceCatalogs(projectId: string, data: ProjectServiceCatalog[], options?: { shouldThrowError?: boolean }): Observable<ProjectServiceCatalog[]> {
        return this.http.post<{ serviceCatalogs: ProjectServiceCatalog[] }>(`${PROJECT_SERVICE_API_ROOT}/${projectId}/service-catalogs`, data).pipe(
            map(({ serviceCatalogs: res }) => res),
            catchError(this.errorService.errorHandler<ProjectServiceCatalog[]>(this.errors, 'Mise à jour du catalogue de services', null, options?.shouldThrowError))
        );
    }

    public getProject<T extends projectEmbed>(
        projectId: string,
        { embedFields = [], includeRemoved = false }: { embedFields?: T[]; includeRemoved?: boolean } = {}
    ): Observable<EmbedProject<T>> {
        const embeds = uniq(embedFields.map((it) => projectConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });
        return this.http.get<Project & { embedded: Embedded }>(
            PROJECT_SERVICE_API_ROOT + '/' + projectId,
            { params: params.set('showDeleted', `${includeRemoved}`) }
        ).pipe(
            map(({ embedded, ...project }) => Project.toCustomProject(project, embedded, embedFields)),
            catchError(this.errorService.errorHandler<EmbedProject<T>>(this.errors, 'Récupération du projet', null))
        );
    }

    /**
     * Find company projects
     * @param {string} companyId
     * @returns {Observable<Project[]>}
     */
    public findProjectsByCompany(companyId: string, filters?: FilterParams): Observable<Project[]> {
        return this.http.get<{ projects: Project[] }>(`${COMPANY_SERVICE_API_ROOT}/${companyId}/projects`, {
            params: toParams(filters)
        }).pipe(
            map(result => result.projects),
            catchError(this.errorService.errorHandler<Project[]>(this.errors, 'Récupération des projets de la société', []))
        );
    }

    // find projects the user is assigned to (DeliveryManager, BackupDeliveryManager, BuildProjectManager, AppManagers)
    public findProjectsByUserId(userId: string): Observable<Project[]> {
        return this.http.get<{ projects: Project[] }>(PROJECT_SERVICE_API_ROOT, {
            params: toParams({
                filter: `assignedTo:${userId}`,
                limit: LIMIT_USER_FORM_PROJECT,
            })
        }).pipe(
            map(result => result.projects),
            catchError(this.errorService.errorHandler<Project[]>(this.errors, 'Récupération des projets de l\'utilisateur', []))
        );
    }

    public findProjectsByUserAndOrCompanyId(userId?: string, companyId?: string): Observable<Project[]> {
        const byUser = isEmpty(userId) ? of([]) : this.findProjectsByUserId(userId);
        const byCompany = isEmpty(companyId) ? of([]) : this.findProjectsByCompany(companyId, {
            limit: LIMIT_USER_FORM_PROJECT,
        });
        return combineLatest([
            byCompany,
            byUser,
        ]).pipe(
            map(result => flatten(result)),
            catchError(this.errorService.errorHandler<Project[]>(
                this.errors,
                'Récupération des projets de l\'utilisateur et/ou de la société',
                []
            ))
        );
    }

    public getInventories(projectId: string, options?: Partial<GetInventoriesOptions>): Observable<{ inventories: Inventory[]; totalSize: number }> {
        return this.http.get<{ inventory: Inventory[]; totalSize: number }>(PROJECT_SERVICE_ASSET_API_ROOT + '/' + projectId + '/inventory', {
            params: {
                ...(options?.filter && { filter: options.filter }),
                ...(options?.fields && { fields: options.fields.join(',') }),
                ...(options?.page !== undefined && { page: options.page.toString() }),
                ...(options?.limit !== undefined && { limit: options.limit.toString() }),
                ...(options?.sort && { sort: options.sort }),
                includeTotal: true
            }
        })
            .pipe(
                map((result) => ({ inventories: result.inventory, totalSize: result.totalSize || 0 })),
                catchError(this.errorService.errorHandler<{ inventories: Inventory[]; totalSize: number }>(
                    this.errors, 'Récupération de l\'inventaire du projet', { inventories: [], totalSize: 0 }))
            );
    }

    public fetchTags(filterParams: FilterParams): Observable<Tag[]> {
        const params = toParams(filterParams);
        return this.http.get<{ tags: Tag[] }>(PROJECT_SERVICE_API_ROOT + '/tags', { params }).pipe(
            map((result) => result.tags),
            catchError(this.errorService.errorHandler<Tag[]>(this.errors, 'Récupération des tags', []))
        );
    }

    public fetchGroups(filterParams: FilterParams): Observable<Array<{ name: string; count: number }>> {
        const params = toParams(filterParams);
        return this.http.get<{ groups: Array<{ name: string; count: number }> }>(PROJECT_SERVICE_API_ROOT + '/groups', { params }).pipe(
            map((result) => result.groups),
            catchError(this.errorService.errorHandler<Array<{ name: string; count: number }>>(
                this.errors,
                'Récupération des groupes',
                []
            ))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(PROJECT_SERVICE_API_ROOT, filterParams, this.errors);
    }

    public createProject(project: Project, options?: { shouldThrowError?: boolean }): Observable<Project> {
        return this.http.post<Project>(PROJECT_SERVICE_API_ROOT, Project.toProjectHttp(project))
            .pipe(
                map(_project => new Project(_project)),
                catchError(this.errorService.errorHandler<Project>(this.errors, 'Création d\'un projet', null, options?.shouldThrowError))
            );
    }

    public updateProject(project: Project, options?: { shouldThrowError?: boolean }): Observable<Project> {
        return this.http.patch<Project>(PROJECT_SERVICE_API_ROOT + `/${project.id}`, project)
            .pipe(
                map(_project => new Project(_project)),
                catchError(this.errorService.errorHandler<Project>(this.errors, 'Modification d\'un projet', null, options?.shouldThrowError))
            );
    }

    public partialUpdateProject(id: string, project: Partial<Project>): Observable<Project> {
        return this.http.patch<Project>(PROJECT_SERVICE_API_ROOT + `/${id}`, project)
            .pipe(
                map(_project => new Project(_project)),
                catchError(this.errorService.errorHandler<Project>(this.errors, 'Modification d\'un projet', null))
            );
    }

    public updateProjectMetadata(projectId: string, metadata: Map<string, string>): Observable<Project> {
        const parsedMetadata = {};
        metadata.forEach((value: string, key: string) => {
            parsedMetadata[key] = value.trim();
        });
        return this.http.post<Project>(PROJECT_SERVICE_API_ROOT + `/${projectId}:setMetadata`, parsedMetadata)
            .pipe(
                map(_project => new Project(_project)),
                catchError(this.errorService.errorHandler<Project>(this.errors, 'Modification des metadatas d\'un projet', null))
            );
    }

    public deleteProject(projectId: string): Observable<string> {
        return this.http.delete<string>(`${PROJECT_SERVICE_API_ROOT}/${projectId}`)
            .pipe(
                catchError(this.errorService.errorHandler<string>(this.errors, 'Suppression d\'un projet', null))
            );
    }

    public exportProjects(filterParams: FilterParams): Observable<{ content: string }> {
        const params = toParams(filterParams);
        return this.http.get<{ content: string }>(`${PROJECT_SERVICE_API_ROOT}:export`, { params }).pipe(
            catchError(this.errorService.errorHandler(this.errors, 'Export des projets', null)),
        );
    }

    public unprovisionProject(data: UnprovisionProjectInventoryRequest): Observable<{parentKey: string}> {
        return this.http.post(environment.cloudFunctionUnprovisioning + UNPROVISION_CLOUD_FUNCTION_API_ROOT, data).pipe(
            catchError(this.errorService.errorHandler(this.errors, 'Déprovisionnement du projet', null))
        );
    }

    public bulkEditProject(projectIds: string[], operations: Operation[]): Observable<BulkEditProjectResponse> {
        return this.http.post<BulkEditProjectResponse>(`${PROJECT_SERVICE_API_ROOT}/bulk-edit`, {
            projectIds,
            operations
        }).pipe(
            catchError(
                this.errorService.errorHandler<BulkEditProjectResponse>(this.errors, 'Modification des projets en masse', null))
        );
    }
}
